import { useCallback } from 'react'
import { usePreferences, type SidebarItem } from './usePreferences'

export type { SidebarItem }

/**
 * Sidebar ordering / visibility / emoji customization.
 * Persists to profiles.preferences.sidebarConfig via usePreferences.
 * Drag-reorder operations use a 400ms trailing debounce to avoid
 * hammering the DB during drag.
 */
export function useSidebarConfig() {
  const { preferences, set } = usePreferences()
  const items = preferences.sidebarConfig

  const setItems = useCallback((
    updater: SidebarItem[] | ((prev: SidebarItem[]) => SidebarItem[]),
  ) => {
    const next = typeof updater === 'function' ? updater(items) : updater
    set('sidebarConfig', next, { debounceMs: 400 })
  }, [items, set])

  const toggleVisibility = useCallback((key: string) => {
    setItems((prev) => prev.map((item) =>
      item.key === key ? { ...item, visible: !item.visible } : item,
    ))
  }, [setItems])

  const setEmoji = useCallback((key: string, emoji: string) => {
    setItems((prev) => prev.map((item) =>
      item.key === key ? { ...item, emoji } : item,
    ))
  }, [setItems])

  const reorder = useCallback((fromIndex: number, toIndex: number) => {
    setItems((prev) => {
      const next = [...prev]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      return next
    })
  }, [setItems])

  return { items, setItems, toggleVisibility, setEmoji, reorder }
}
